import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { openVoipDialer } from '../components/voip/voipDialerBus';
import api from '../api/axios';
import { useAuth } from './AuthContext';

// Port 1:1 từ Core admin/src/contexts/VoipContext.jsx. KHÁC Core (adapt SaaS multi-tenant):
//  - Creds: /cnv-call/webphone-credentials (fetch live, trả sip_password/tenant_domain/sip_server)
//    thay vì /etelecom/my-extension (Core lưu password local).
//  - Gate: user.module_seats.cnv_call (per-user seat) thay vì tenant config flag.
//  - Endpoint call-logs/sync đổi /etelecom/* → /cnv-call/*.
//  - FIX bug Core: nhãn hướng cuộc gọi (Core so 'in' nhưng sipClient bắn 'inbound', và ở
//    state idle direction=null) → dùng callDirectionRef giữ hướng của cuộc gọi vừa xong.
const VoipContext = createContext(null);

// ── Ringtone using HTML5 Audio (no user interaction required after first click) ──
function createRingtone() {
  let audio = null;
  let playing = false;

  function start() {
    if (playing) return;
    playing = true;
    try {
      if (!audio) {
        audio = new Audio('/ringtone.wav');
        audio.loop = true;
        audio.volume = 0.5;
        audio.preload = 'auto';
      }
      audio.currentTime = 0;
      const p = audio.play();
      if (p && p.catch) p.catch(() => {}); // ignore autoplay block
    } catch {}
  }

  function stop() {
    playing = false;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }

  // Preload audio on first user interaction (unlock autoplay)
  function preload() {
    if (!audio) {
      audio = new Audio('/ringtone.wav');
      audio.loop = true;
      audio.volume = 0.5;
      audio.preload = 'auto';
    }
    // Play + immediately pause to unlock autoplay
    audio.volume = 0;
    const p = audio.play();
    if (p && p.then) p.then(() => { audio.pause(); audio.volume = 0.5; audio.currentTime = 0; }).catch(() => {});
  }

  return { start, stop, preload };
}

export function VoipProvider({ children }) {
  const { user } = useAuth();
  const sipRef = useRef(null);
  const ringtoneRef = useRef(createRingtone());
  const [ringtoneMuted, setRingtoneMuted] = useState(false);
  const ringtoneMutedRef = useRef(false);

  // SIP registration state
  const [sipRegistered, setSipRegistered] = useState(false);
  // 'idle' (chưa có máy nhánh) | 'connecting' | 'registered' | 'disconnected'
  const [sipStatus, setSipStatus] = useState('idle');
  const [extensionInfo, setExtensionInfo] = useState(null);
  // Chặn đua giữa cleanup và register: mỗi lần chạy effect tăng 1, kết quả của
  // lần cũ về sau bị bỏ (không thì client mồ côi vẫn giữ đăng ký sau khi logout).
  const genRef = useRef(0);
  const credsRetryRef = useRef(null);
  // Chặn 2 lượt lấy creds chạy song song → 2 SipClient cùng đăng ký 1 máy nhánh
  // (client cũ thành mồ côi nhưng VẪN giữ đăng ký với tổng đài → cuộc gọi đến
  // có thể rơi vào cái không ai nghe).
  const fetchingRef = useRef(false);

  // Call state: idle | ringing | calling | connecting | in_call
  const [callState, setCallState] = useState('idle');
  const [callDirection, setCallDirection] = useState(null);
  const [callNumber, setCallNumber] = useState(null);
  const [callStartTime, setCallStartTime] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const callActivityRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const callDirectionRef = useRef(null);   // giữ hướng cuộc gọi để log đúng khi về idle (direction=null)
  const callNumberRef = useRef(null);      // giữ số để ghi vào subject khi về idle (number=null, tránh stale-closure)
  // Noti "Cuộc gọi đến" — giữ ref để đóng khi hết đổ chuông (trả lời/từ chối/
  // nhỡ) thay vì treo hoài. + timeout an toàn nếu state change không bắn.
  const incomingNotifRef = useRef(null);
  const incomingNotifTimerRef = useRef(null);
  const closeIncomingNotif = useCallback(() => {
    if (incomingNotifTimerRef.current) { clearTimeout(incomingNotifTimerRef.current); incomingNotifTimerRef.current = null; }
    if (incomingNotifRef.current) { try { incomingNotifRef.current.close(); } catch {} incomingNotifRef.current = null; }
  }, []);

  // CRM entity match (enhanced)
  const [callerInfo, setCallerInfo] = useState(null);

  const startRingtone = () => {
    if (!ringtoneMutedRef.current) ringtoneRef.current?.start();
  };

  const stopRingtone = () => {
    ringtoneRef.current?.stop();
  };

  const muteRingtone = useCallback(() => {
    ringtoneRef.current?.stop();
    ringtoneMutedRef.current = true;
    setRingtoneMuted(true);
  }, []);

  // Fetch SIP credentials khi user login.
  // Gate: chỉ gọi khi user có seat cnv_call (per-user) — endpoint luôn mount nhưng seat-gated
  // (402 nếu chưa gán). Tránh gọi thừa mỗi login cho user không dùng tổng đài.
  useEffect(() => {
    if (!user) { cleanup(); return; }
    if (user?.module_seats?.cnv_call) fetchExtension();
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.module_seats?.cnv_call]);

  function cleanup() {
    genRef.current += 1;                      // vô hiệu hoá mọi việc đang chạy dở
    if (credsRetryRef.current) { clearTimeout(credsRetryRef.current); credsRetryRef.current = null; }
    if (sipRef.current) { sipRef.current.unregister(); sipRef.current = null; }
    stopRingtone();
    setSipRegistered(false);
    setSipStatus('idle');
    setExtensionInfo(null);
    setCallState('idle');
  }

  // Lấy creds. Hỏng vì mạng/BE thì THỬ LẠI giãn dần — bản cũ chỉ console.warn rồi
  // thôi, một lần lỗi thoáng qua là nút gọi biến mất tới khi F5.
  async function fetchExtension(attempt = 0) {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    const gen = genRef.current;
    try {
      setSipStatus((s) => (s === 'registered' ? s : 'connecting'));
      const res = await api.get('/cnv-call/webphone-credentials');
      if (gen !== genRef.current) return;
      const ext = res.data?.data;
      // data:null = chưa được gán máy nhánh → im lặng, đó không phải lỗi.
      if (!ext?.extension_number) { setSipStatus('idle'); return; }
      // Có máy nhánh nhưng tổng đài chưa trả mật khẩu/WSS = lỗi TẠM phía nhà cung
      // cấp → phải thử lại, đừng bỏ luôn (nút gọi biến mất tới khi F5).
      if (!ext.sip_password) {
        setSipStatus('disconnected');
        const delay = Math.min(60, 5 * 2 ** attempt) * 1000;
        credsRetryRef.current = setTimeout(() => {
          credsRetryRef.current = null;
          if (gen === genRef.current) fetchExtension(attempt + 1);
        }, delay);
        return;
      }
      setExtensionInfo(ext);
      await registerSip(ext, gen);
    } catch (err) {
      if (gen !== genRef.current) return;
      const code = err?.response?.status;
      console.warn('[VoIP] Không lấy được extension:', err.message);
      // 402/403 = chưa được gán máy nhánh / không có quyền → thử lại vô ích.
      if (code === 402 || code === 403) { setSipStatus('idle'); return; }
      setSipStatus('disconnected');
      const delay = Math.min(60, 5 * 2 ** attempt) * 1000;
      credsRetryRef.current = setTimeout(() => {
        credsRetryRef.current = null;
        if (gen === genRef.current) fetchExtension(attempt + 1);
      }, delay);
    } finally {
      fetchingRef.current = false;
    }
  }

  async function registerSip(ext, gen = genRef.current) {
    try {
      const { default: SipClient } = await import('../services/sipClient');
      if (gen !== genRef.current) return;
      // Có client cũ thì GỠ trước — không thì nó vẫn giữ đăng ký với tổng đài.
      if (sipRef.current) { sipRef.current.unregister(); sipRef.current = null; }
      const client = new SipClient();

      client.onRegistered = () => setSipRegistered(true);
      client.onUnregistered = () => setSipRegistered(false);
      client.onStatusChange = (status) => setSipStatus(status);

      client.onIncomingCall = async (callerNumber) => {
        ringtoneMutedRef.current = false;
        setRingtoneMuted(false); // reset mute for new call
        startRingtone();

        // Tra người gọi: BE làm hết trong 1 request (/cnv-call/caller-lookup) — khớp SĐT
        // theo kernel phoneMatchVariants ('0933…'/'84933…'/'+84933…' về cùng một người)
        // và nhận cả Zalo User ID, thứ cuộc gọi ZCC mang thay cho số điện thoại.
        try {
          const res = await api.get('/cnv-call/caller-lookup', { params: { number: callerNumber } });
          const d = res.data?.data || {};
          if (d.found) {
            const info = {
              type: d.type, id: d.id, name: d.name, phone: d.phone,
              company: d.company, job_title: d.job_title, channel: d.channel,
              // zalo_user_id phải đi kèm: khung cuộc gọi dựa vào nó để cho sửa lại
              // tên/SĐT của khách Zalo (tên đó dùng cho mọi cuộc gọi sau + báo cáo).
              zalo_user_id: d.zalo_user_id || null,
              deals: d.deals || [], call_status: d.call_status,
            };
            try {
              const cRes = await api.get(`/cnv-call/call-logs/entity/${d.type}/${d.id}`);
              const logs = cRes.data?.data || [];
              info.callCount = logs.length;
              info.lastCall = logs[0]?.started_at;
            } catch { /* lịch sử gọi hỏng không được che mất hồ sơ khách */ }
            setCallerInfo(info);
          } else if (d.needs_identify) {
            // Cuộc gọi Zalo của người chưa ai đặt tên: KHÔNG để trống khung cuộc gọi —
            // widget hiện ô nhập tên + SĐT ngay, điền xong là bung hồ sơ CRM.
            setCallerInfo({
              needs_identify: true, channel: 'zalo', zalo_user_id: d.zalo_user_id,
              name: d.known_name || null, avatar: d.avatar || null,
            });
          } else {
            // Số thường không có trong CRM → giữ nguyên hành vi cũ: thử tra nhân viên.
            try {
              const res3 = await api.get(`/users/simple?search=${encodeURIComponent(callerNumber)}&limit=1`);
              const emp = (res3.data?.data || [])[0];
              if (emp) {
                setCallerInfo({
                  type: 'employee', id: emp.id, name: emp.name,
                  phone: emp.phone, company: emp.department || emp.position,
                  avatar: emp.avatar, status: emp.is_active ? 'active' : 'inactive',
                });
              } else {
                setCallerInfo(null);
              }
            } catch { setCallerInfo(null); }
          }
        } catch { setCallerInfo(null); }

        if (document.hidden && Notification.permission === 'granted') {
          closeIncomingNotif(); // dọn noti cũ nếu có
          try {
            const n = new Notification('Cuộc gọi đến', { body: `Số: ${callerNumber}`, icon: '/favicon.png', tag: 'incoming-call', requireInteraction: true });
            n.onclick = () => { try { window.focus(); } catch {} try { n.close(); } catch {} };
            incomingNotifRef.current = n;
            // An toàn: tự đóng sau 30s (quá thời gian đổ chuông) nếu state change
            // không kịp đóng → không treo hoài.
            incomingNotifTimerRef.current = setTimeout(closeIncomingNotif, 30000);
          } catch {}
        }
      };

      client.onCallStateChange = (state, number, direction) => {
        setCallState(state);
        setCallNumber(number);
        setCallDirection(direction);
        if (direction) callDirectionRef.current = direction;   // nhớ hướng (idle bắn direction=null)
        if (number) callNumberRef.current = number;            // nhớ số (idle bắn number=null)

        if (state !== 'ringing') { stopRingtone(); closeIncomingNotif(); }

        if (state === 'in_call') {
          const now = Date.now();
          setCallStartTime(now);
          callStartTimeRef.current = now;
        }
        if (state === 'idle') {
          stopRingtone();
          if (callStartTimeRef.current || callActivityRef.current) {
            setTimeout(() => {
              api.post('/cnv-call/sync-call-logs', { date_from: new Date().toISOString().split('T')[0] }).catch(() => {});
            }, 3000);
          }
          if (callActivityRef.current) {
            const duration = callStartTimeRef.current ? Math.floor((Date.now() - callStartTimeRef.current) / 1000) : 0;
            const outcome = duration > 0 ? 'Đã nghe' : 'Không nghe';
            const dir = callDirectionRef.current;
            api.put(`/crm/activities/${callActivityRef.current}`, {
              is_done: true, done_at: new Date().toISOString(), outcome,
              duration_minutes: Math.ceil(duration / 60) || 1,
              subject: `${dir === 'inbound' ? 'Gọi đến' : 'Gọi đi'} ${number || callNumberRef.current || ''} — ${outcome} (${Math.floor(duration / 60)}p${(duration % 60).toString().padStart(2, '0')}s)`,
            }).then(() => {
              window.dispatchEvent(new CustomEvent('voip-activity-updated'));
            }).catch(() => {});
            // Trạng thái gọi của lead do MÁY CHỦ quyết (cnvCallService.syncCallLogs, qua
            // kernel callLog) — trình duyệt không tự đặt nữa. Bản cũ suy từ `duration > 0`
            // nên: cuộc gọi ĐẾN từ lead không bao giờ được tính (không gắn lead nào), gọi
            // từ máy bàn cùng máy nhánh cũng không, và `call_state='missed'` mà tổng đài
            // ghi duration>0 thì bị đánh dấu nhầm là "đã gọi".
            // Đồng bộ CDR chạy ngay sau cuộc gọi (POST /cnv-call/sync-call-logs ở trên).
            callActivityRef.current = null;
          }
          callStartTimeRef.current = null;
          callDirectionRef.current = null;
          callNumberRef.current = null;
          setCallStartTime(null);
          setCallerInfo(null);
          setIsMuted(false);
          ringtoneMutedRef.current = false;
          setRingtoneMuted(false);
        }
      };

      // Gán TRƯỚC khi await: register() có thể mất vài giây, trong lúc đó cleanup
      // chạy thì phải gỡ được đúng client này, không để nó sống mồ côi.
      sipRef.current = client;
      await client.register(ext.extension_number, ext.sip_password, ext.tenant_domain, ext.sip_server);
      if (gen !== genRef.current) { client.unregister(); return; }
    } catch (err) {
      console.error('[VoIP] SIP register failed:', err);
      // Client vẫn đang đăng ký tốt thì ĐỪNG đụng vào: lấy lại creds sẽ kéo theo
      // unregister() ở đầu registerSip, biến một lỗi tạm thành vòng lặp gỡ-dựng máy nhánh.
      if (sipRef.current?.registered) return;
      setSipStatus('disconnected');
      // Đăng ký hỏng ngay từ đầu: creds có thể đã đổi (đổi máy nhánh, đổi mật
      // khẩu) → lấy lại creds sau ít giây thay vì nằm im.
      credsRetryRef.current = setTimeout(() => {
        credsRetryRef.current = null;
        if (gen === genRef.current) fetchExtension(1);
      }, 10000);
    }
  }

  // Tự chữa: soát mỗi 30s + mỗi lần tab hiện lại / có mạng lại / máy tỉnh dậy.
  // WebSocket bị treo lúc laptop ngủ KHÔNG phải lúc nào cũng bắn onDisconnect →
  // chỉ dựa vào sự kiện là có lúc kẹt offline vĩnh viễn.
  useEffect(() => {
    if (!user?.module_seats?.cnv_call) return undefined;
    const kick = () => {
      if (document.hidden) return;
      if (sipRef.current) sipRef.current.ensureRegistered();
      else if (!credsRetryRef.current && !extensionInfo) fetchExtension();
    };
    const timer = setInterval(kick, 30000);
    document.addEventListener('visibilitychange', kick);
    window.addEventListener('online', kick);
    window.addEventListener('focus', kick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', kick);
      window.removeEventListener('online', kick);
      window.removeEventListener('focus', kick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.module_seats?.cnv_call, extensionInfo]);

  // Bấm tay từ UI ("Kết nối lại").
  const reconnectVoip = useCallback(() => {
    if (sipRef.current) sipRef.current.ensureRegistered();
    else fetchExtension();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callEntityRef = useRef(null);

  const makeCall = useCallback(async (phoneNumber, entity) => {
    if (!sipRef.current) throw new Error('VoIP chưa sẵn sàng');
    callEntityRef.current = entity || null;
    await sipRef.current.call(phoneNumber);
    try {
      const body = { type: 'call', subject: `Đang gọi ${phoneNumber}...`, due_date: new Date().toISOString().split('T')[0], due_time: new Date().toTimeString().slice(0, 5) };
      if (entity?.type === 'lead') body.lead_id = entity.id;
      else if (entity?.type === 'contact') body.contact_id = entity.id;
      else if (entity?.type === 'company') body.company_id = entity.id;
      else if (entity?.type === 'deal') body.deal_id = entity.id;
      const res = await api.post('/crm/activities', body);
      callActivityRef.current = res.data?.data?.id || null;
      window.dispatchEvent(new CustomEvent('voip-activity-created'));
    } catch {}
  }, []);

  const answerCall = useCallback(() => { stopRingtone(); sipRef.current?.answer(); }, []);
  const rejectCall = useCallback(() => { stopRingtone(); sipRef.current?.reject(); setCallState('idle'); setCallerInfo(null); ringtoneMutedRef.current = false; setRingtoneMuted(false); }, []);
  const hangupCall = useCallback(() => { stopRingtone(); sipRef.current?.hangup(); setCallState('idle'); setCallerInfo(null); }, []);

  const toggleMute = useCallback(() => {
    if (!sipRef.current) return;
    if (isMuted) { sipRef.current.unmute(); setIsMuted(false); }
    else { sipRef.current.mute(); setIsMuted(true); }
  }, [isMuted]);

  useEffect(() => {
    if (user && 'Notification' in window && Notification.permission === 'default') Notification.requestPermission();
  }, [user]);

  // Preload ringtone audio on first user interaction (unlock browser autoplay)
  useEffect(() => {
    if (!user) return;
    const unlock = () => {
      ringtoneRef.current?.preload();
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    return () => { document.removeEventListener('click', unlock); document.removeEventListener('keydown', unlock); };
  }, [user]);

  return (
    <VoipContext.Provider value={{
      sipRegistered, sipStatus, reconnectVoip, extensionInfo, callState, callDirection, callNumber, callStartTime,
      // setCallerInfo mở ra ngoài để khung cuộc gọi cập nhật hồ sơ ngay sau khi nhân
      // viên điền tên + SĐT cho khách gọi qua Zalo (không phải chờ cuộc gọi sau).
      callerInfo, setCallerInfo, isMuted, ringtoneMuted, makeCall, answerCall, rejectCall, hangupCall, toggleMute, muteRingtone,
      // Mở bàn phím quay số từ BẤT KỲ đâu (trang Tổng đài, menu avatar…). Đưa vào hợp
      // đồng useVoip() để cnv-ui gọi được mà không phải import bus riêng của từng host —
      // hai nền tảng cùng một cách.
      openDialer: openVoipDialer,
    }}>
      {children}
    </VoipContext.Provider>
  );
}

/**
 * Bản KHÔNG NÉM của useVoip — trả null khi host chưa mount VoipProvider.
 *
 * Dành cho code DÙNG CHUNG (cnv-ui): trang Cài đặt phải mở được kể cả khi tổng đài chưa
 * bật. Bọc useVoip() trong try/catch là vi phạm Rules of Hooks (hook gọi có điều kiện) —
 * eslint chặn commit, và đúng ra phải chặn.
 */
export function useVoipOptional() {
  return useContext(VoipContext) || null;
}

export function useVoip() {
  const ctx = useContext(VoipContext);
  if (!ctx) throw new Error('useVoip must be inside VoipProvider');
  return ctx;
}
